import { evaluteWithErrors as solve } from "@/utils/math";
import { atom } from "jotai";
import { DollarAmount } from "../types";
import { createEpochAtoms } from "./epoch";
import { createFocusAtom, generalInputAtom } from "./inputHelpers";
import { getSummaryValueAtIndex } from "./itemization";
import { CellTypes, ItemizationLineItem } from "./types";
import {
  createAggregatedFormulaItemization,
  createComputedFormulaRowMolecule,
} from "./workingCapitalEpoch";

export const workingCapitalEpochsAtomsContainer = createEpochAtoms(
  "workingCapitalEpochs",
);

export const workingCapitalFormulaOptions = [
  {
    id: "average",
    label: "Full Period",
    title: "Full Period Average",
    description: "Calculates the average of all the entered periods.",
  },
  {
    id: "ttm",
    label: "TTM",
    title: "Trailing Twelve Months",
    description: "Calculates the average of the most recent 12 periods.",
  },
  {
    id: "max",
    label: "Max",
    title: "Maximum",
    description: "Uses the period with the highest net working capital amount.",
  },
  {
    id: "min",
    label: "Min",
    title: "Minimum",
    description: "Uses the period with the lowest net working capital amount.",
  },
  {
    id: "recent",
    label: "Recent",
    title: "Most Recent",
    description: "Uses the most recent period based on date.",
  },
] as const;

export const workingCapitalFormulaIdAtom = createFocusAtom<
  (typeof workingCapitalFormulaOptions)[number]["id"]
>("wcFormula", workingCapitalFormulaOptions[0].id);

export const workingCapitalFormulaAtom = atom((get) => {
  const formulaId = get(workingCapitalFormulaIdAtom);
  return (
    workingCapitalFormulaOptions.find((option) => option.id === formulaId) ||
    workingCapitalFormulaOptions[0]
  );
});

export const periodFormatOptions = [
  {
    id: "monthly",
    label: "Monthly",
  },
  {
    id: "quarterly",
    label: "Quarterly",
  },
  {
    id: "annually",
    label: "Annually",
  },
] as const;

export const periodFormatIdAtom = createFocusAtom<
  (typeof periodFormatOptions)[number]["id"]
>("wcPeriodFormat", periodFormatOptions[0].id);

export const periodMultiplierAtom = atom((get) => {
  const periodFormatId = get(periodFormatIdAtom);
  switch (periodFormatId) {
    case "monthly":
      return 12;
    case "quarterly":
      return 4;
    case "annually":
      return 1;
    default:
      return 1;
  }
});

export const sellerProvidedCashAtom = generalInputAtom<DollarAmount>(
  "wcSellerProvidedCash",
  null,
);

export const sellerProvidedAccountRecAtom = generalInputAtom<DollarAmount>(
  "wcSellerProvidedAccountRec",
  null,
);

export const sellerProvidedInventoryAtom = generalInputAtom<DollarAmount>(
  "wcSellerProvidedInventory",
  null,
);

export const sellerProvidedPrepaidExpensesAtom = generalInputAtom<DollarAmount>(
  "wcSellerProvidedPrepaidExpenses",
  null,
);

export const sellerProvidedOtherCurrentAssetsAtom =
  generalInputAtom<DollarAmount>("wcSellerProvidedOtherCurrentAssets", null);

export const sellerProvidedTotalAtom = atom((get) => {
  return solve<DollarAmount>("a + b + c + d + e", {
    a: Number(get(sellerProvidedCashAtom)) || 0,
    b: Number(get(sellerProvidedAccountRecAtom)) || 0,
    c: Number(get(sellerProvidedInventoryAtom)) || 0,
    d: Number(get(sellerProvidedPrepaidExpensesAtom)) || 0,
    e: Number(get(sellerProvidedOtherCurrentAssetsAtom)) || 0,
  });
});

export const grossRevenueItemization = createAggregatedFormulaItemization(
  "workingcapital|grossRevenue",
);

export const costOfGoodsSoldItemization = createAggregatedFormulaItemization(
  "workingcapital|cogs",
  {
    isNegative: true,
  },
);

export const grossProfitMolecule = createComputedFormulaRowMolecule((i) => {
  return atom((get) => {
    return {
      type: "dollar",
      isLocked: true,
      value: solve<DollarAmount>("revenue - cogs", {
        revenue: getSummaryValueAtIndex(grossRevenueItemization, i, get) || 0,
        cogs: getSummaryValueAtIndex(costOfGoodsSoldItemization, i, get) || 0,
      }),
    };
  });
});

export const grossMarginsMolecule = createComputedFormulaRowMolecule((i) => {
  return atom((get) => {
    const grossProfit = get(get(grossProfitMolecule)[i]);

    const revenue =
      getSummaryValueAtIndex(grossRevenueItemization, i, get) || 0;

    return {
      type: "percent",
      isLocked: true,
      value: solve<number>("profit / revenue", {
        profit: Number(grossProfit.value || 0),
        revenue,
      }),
    };
  });
});

export const fixedOperatingExpensesItemization =
  createAggregatedFormulaItemization("workingcapital|fixedOperatingExpenses", {
    isNegative: true,
  });

export const fixedOpExAsPercentOfRevenueMolecule =
  createComputedFormulaRowMolecule((i) => {
    return atom((get) => {
      const fixedOpEx = getSummaryValueAtIndex(
        fixedOperatingExpensesItemization,
        i,
        get,
      );

      const revenue =
        getSummaryValueAtIndex(grossRevenueItemization, i, get) || 0;

      return {
        type: "percent",
        isLocked: true,
        value: solve<number>("opEx / revenue", {
          opEx: Number(fixedOpEx || 0),
          revenue,
        }),
      };
    });
  });

// CURRENT ASSETS & LIABILITES

const currentAssetsStaticLineItems: ItemizationLineItem[] = [
  { id: "accountsReceivable", label: "Accounts Receivable (A/R)" },
  { id: "inventory", label: "Inventory" },
  { id: "workInProgress", label: "Work-in-Progress (WIP)" },
  { id: "prepaidExpenses", label: "Prepaid Expenses" },
];

export const currentAssetsItemization = createAggregatedFormulaItemization(
  "workingcapital|currentAssets",
  { staticLineItems: currentAssetsStaticLineItems },
);

const currentLiabilitiesStaticLineItems: ItemizationLineItem[] = [
  { id: "accountsPayable", label: "Accounts Payable (A/P)" },
  { id: "workInProgress", label: "Work-in-Progress (WIP)" },
  { id: "accuedPayrollBenifits", label: "Accrued Payroll & Benefits" },
];

export const currentLiabilitiesItemization = createAggregatedFormulaItemization(
  "workingcapital|currentLiabilities",
  { staticLineItems: currentLiabilitiesStaticLineItems },
);

export const netWorkingCapitalMolecule = createComputedFormulaRowMolecule(
  (i) => {
    return atom((get) => {
      const currentAssets = getSummaryValueAtIndex(
        currentAssetsItemization,
        i,
        get,
      );

      const currentLiabilities = getSummaryValueAtIndex(
        currentLiabilitiesItemization,
        i,
        get,
      );

      return {
        type: "dollar",
        isLocked: true,
        value: solve<DollarAmount>("assets - liabilities", {
          assets: Number(currentAssets || 0),
          liabilities: Number(currentLiabilities || 0),
        }),
      };
    });
  },
);

export const changeInNetWorkingCapitalMolecule =
  createComputedFormulaRowMolecule((i) => {
    return atom((get) => {
      if (i <= 1) {
        return {
          type: "text",
          isLocked: true,
          value: "-",
        } as CellTypes;
      }

      const currentNWC = get(get(netWorkingCapitalMolecule)[i]);
      const prevNWC = get(get(netWorkingCapitalMolecule)[i - 1]);

      return {
        type: "dollar",
        isLocked: true,
        value: solve<DollarAmount>("current - prev", {
          current: Number(currentNWC.value || 0),
          prev: Number(prevNWC.value || 0),
        }),
      };
    });
  });

export const changeInNetWorkingCapitalPercentMolecule =
  createComputedFormulaRowMolecule((i) => {
    return atom((get) => {
      if (i <= 1) {
        return {
          type: "text",
          isLocked: true,
          value: "-",
        } as CellTypes;
      }

      const currentNWC = get(get(netWorkingCapitalMolecule)[i]);
      const prevNWC = get(get(netWorkingCapitalMolecule)[i - 1]);

      return {
        type: "percent",
        isLocked: true,
        value: solve<number>("current / prev - 1", {
          current: Number(currentNWC.value || 0),
          prev: Number(prevNWC.value || 0),
        }),
      };
    });
  });

export const nwcAsPercentOfRevenueMolecule = createComputedFormulaRowMolecule(
  (i) => {
    return atom((get) => {
      const nwc = get(get(netWorkingCapitalMolecule)[i]).value;
      const revenue =
        getSummaryValueAtIndex(grossRevenueItemization, i, get) || 0;

      const periodMultiplier = get(periodMultiplierAtom);

      return {
        type: "percent",
        isLocked: true,
        value: solve<number>("nwc / (revenue * periodMultiplier)", {
          nwc: Number(nwc || 0),
          revenue,
          periodMultiplier,
        }),
      };
    });
  },
);

export const quickRatioMolecule = createComputedFormulaRowMolecule((i) => {
  return atom((get) => {
    const currentAssets = getSummaryValueAtIndex(
      currentAssetsItemization,
      i,
      get,
    );

    const indexOfInventoryLineItem = currentAssetsStaticLineItems.findIndex(
      (item) => item.id === "inventory",
    );
    const inventoryValues = get(
      get(currentAssetsItemization).lineItemsMolecules[
        indexOfInventoryLineItem
      ],
    ).map((a) => get(a).value);

    const inventory = inventoryValues[i];

    const currentLiabilities = getSummaryValueAtIndex(
      currentLiabilitiesItemization,
      i,
      get,
    );

    const value = solve<number>("(assets - inventory) / liabilities", {
      assets: Number(currentAssets || 0),
      liabilities: Number(currentLiabilities || 0),
      inventory: Number(inventory || 0),
    });

    return {
      type: "multiple",
      isLocked: true,
      value,
    };
  });
});

export const currentRatioMolecule = createComputedFormulaRowMolecule((i) => {
  return atom((get) => {
    const currentAssets = getSummaryValueAtIndex(
      currentAssetsItemization,
      i,
      get,
    );
    const currentLiabilities = getSummaryValueAtIndex(
      currentLiabilitiesItemization,
      i,
      get,
    );

    const value = solve<number>("assets / liabilities", {
      assets: Number(currentAssets || 0),
      liabilities: Number(currentLiabilities || 0),
    });

    return {
      type: "multiple",
      isLocked: true,
      value,
    };
  });
});

// Working Capital Efficiency Metrics

export const daysSalesOutstandingMolecule = createComputedFormulaRowMolecule(
  (i) => {
    return atom((get) => {
      const indexOfAccountsReceivableLineItem =
        currentAssetsStaticLineItems.findIndex(
          (item) => item.id === "accountsReceivable",
        );
      const accountsReceivableValues = get(
        get(currentAssetsItemization).lineItemsMolecules[
          indexOfAccountsReceivableLineItem
        ],
      ).map((a) => get(a).value);

      const accountsReceivable = accountsReceivableValues[i];

      const revenue =
        getSummaryValueAtIndex(grossRevenueItemization, i, get) || 0;

      const periodMultiplier = get(periodMultiplierAtom);

      const value = solve<number>(
        "(receivable / revenue) * (365 / periodMultiplier)",
        {
          receivable: Number(accountsReceivable || 0),
          revenue,
          periodMultiplier,
        },
      );

      return {
        type: "day",
        isLocked: true,
        value,
      };
    });
  },
);

export const daysInventoryOutstandingMolecule =
  createComputedFormulaRowMolecule((i) => {
    return atom((get) => {
      const indexOfInventoryLineItem = currentAssetsStaticLineItems.findIndex(
        (item) => item.id === "inventory",
      );
      const inventoryValues = get(
        get(currentAssetsItemization).lineItemsMolecules[
          indexOfInventoryLineItem
        ],
      ).map((a) => get(a).value);

      const inventory = inventoryValues[i];

      const cogs = getSummaryValueAtIndex(costOfGoodsSoldItemization, i, get);

      const periodMultiplier = get(periodMultiplierAtom);

      const value = solve<number>(
        "(inventory / cogs) * (365 / periodMultiplier)",
        {
          inventory: Number(inventory || 0),
          cogs,
          periodMultiplier,
        },
      );

      return {
        type: "day",
        isLocked: true,
        value,
      };
    });
  });

export const daysPayableOutstandingMolecule = createComputedFormulaRowMolecule(
  (i) => {
    return atom((get) => {
      const indexOfAccountsPayableLineItem =
        currentLiabilitiesStaticLineItems.findIndex(
          (item) => item.id === "accountsPayable",
        );
      const accountsPayableValues = get(
        get(currentLiabilitiesItemization).lineItemsMolecules[
          indexOfAccountsPayableLineItem
        ],
      ).map((a) => get(a).value);

      const accountsPayable = accountsPayableValues[i];

      const cogs = getSummaryValueAtIndex(costOfGoodsSoldItemization, i, get);

      const periodMultiplier = get(periodMultiplierAtom);

      const value = solve<number>(
        "(payable / cogs) * (365 / periodMultiplier)",
        {
          payable: Number(accountsPayable || 0),
          cogs,
          periodMultiplier,
        },
      );

      return {
        type: "day",
        isLocked: true,
        value,
      };
    });
  },
);

export const cashConversionCycleMolecule = createComputedFormulaRowMolecule(
  (i) => {
    return atom((get) => {
      const dso = get(get(daysSalesOutstandingMolecule)[i]);
      const dio = get(get(daysInventoryOutstandingMolecule)[i]);
      const dpo = get(get(daysPayableOutstandingMolecule)[i]);

      const value = solve<number>("(dso + dio) - dpo", {
        dso: Number(dso.value),
        dio: Number(dio.value),
        dpo: Number(dpo.value),
      });

      return {
        type: "day",
        isLocked: true,
        value,
      };
    });
  },
);

export const netWorkingCapitalRequiredAtClosingAtom = atom((get) => {
  return solve<DollarAmount>(
    "(totalFixedOperatingExpenses) * (ccc / (365 / periodMultiplier))",
    {
      periodMultiplier: get(periodMultiplierAtom),
      ccc: get(get(cashConversionCycleMolecule)[0]).value,
      totalFixedOperatingExpenses: getSummaryValueAtIndex(
        fixedOperatingExpensesItemization,
        0,
        get,
      ),
    },
  );
});

export const buyersWCAdjustmentAtom = atom((get) => {
  return solve<DollarAmount>("nwcReqAtClose - totalSellerWC", {
    nwcReqAtClose: get(netWorkingCapitalRequiredAtClosingAtom),
    totalSellerWC: get(sellerProvidedTotalAtom),
  });
});
